import { useMemo, useState } from "react";
import { Landmark, Lock } from "lucide-react";
import { toast } from "sonner";
import { requestGovAccess } from "@/lib/gov-integration-api";

function isEligibleGovEmail(email) {
  const cleaned = String(email || "").trim().toLowerCase();
  if (!cleaned.includes("@")) return false;
  const domain = cleaned.split("@").pop() || "";
  const labels = domain.split(".").filter(Boolean);
  return labels.some((p) => p === "gov" || p === "edu" || p === "school");
}

/**
 * Locked state for Government integration on Starter / Studio / Scale.
 * Upgrade request requires a .gov / .edu / .school contact email.
 */
export default function GovUpgradePrompt({
  plan = "studio",
  defaultEmail = "",
  compact = false,
  className = "",
}) {
  const currentPlan = String(plan || "studio").trim() || "studio";
  const [contactEmail, setContactEmail] = useState(String(defaultEmail || "").trim());
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const eligible = useMemo(() => isEligibleGovEmail(contactEmail), [contactEmail]);

  const submit = async () => {
    if (!eligible) {
      toast.error("Use a .gov, .edu, or .school email address");
      return;
    }
    setSubmitting(true);
    try {
      await requestGovAccess({ contactEmail, notes });
      setSubmitted(true);
      toast.success("Upgrade request submitted");
    } catch (err) {
      toast.error(err?.message || "Could not submit request");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`space-y-4 ${className}`.trim()} data-testid="gov-upgrade-prompt">
      {!compact ? (
        <h2 className="font-heading text-2xl flex items-center gap-2">
          <Landmark size={20} /> Government integration
        </h2>
      ) : null}
      <div className="surface p-6 border border-[var(--border-default)] max-w-xl">
        <div className="flex items-start gap-3">
          <Lock size={18} className="text-[var(--text-tertiary)] shrink-0 mt-0.5" aria-hidden />
          <div className="w-full space-y-4">
            <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
              FormSG + Plumber government registration is an{" "}
              <strong className="text-[var(--text-primary)]">Enterprise</strong> capability.
              Your workspace is on{" "}
              <strong className="text-[var(--text-primary)]">{currentPlan}</strong>.
            </p>
            <p className="text-xs text-[var(--text-tertiary)] leading-relaxed">
              Access requests must come from a public-sector or education email ending in{" "}
              <strong>.gov</strong>, <strong>.edu</strong>, or <strong>.school</strong>
              {" "}(for example <span className="font-mono">name@agency.gov.sg</span>).
            </p>

            {submitted ? (
              <p className="text-sm text-[var(--text-primary)]">
                Request sent. Our team will review and enable Government integration on Enterprise.
              </p>
            ) : (
              <>
                <div>
                  <label className="text-overline block mb-2">Requestor email</label>
                  <input
                    className="input-dark w-full"
                    type="email"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    placeholder="name@agency.gov.sg"
                    data-testid="gov-request-email"
                  />
                  {contactEmail && !eligible ? (
                    <p className="text-xs text-red-500 mt-1">Must be a .gov, .edu, or .school address</p>
                  ) : null}
                </div>
                <div>
                  <label className="text-overline block mb-2">Notes (optional)</label>
                  <textarea
                    className="input-dark w-full min-h-[80px]"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Agency, programme, or FormSG use case"
                  />
                </div>
                <button
                  type="button"
                  className="btn-primary text-xs"
                  disabled={submitting || !eligible}
                  onClick={submit}
                  data-testid="gov-request-submit"
                >
                  {submitting ? "Submitting…" : "Request Enterprise upgrade"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
